import React from 'react';
import {
  Paper,
  Typography,
  Divider,
  Tooltip,
  IconButton,
  Chip,
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  SmartToy as TeammateIcon,
  Person as HumanIcon,
  Link as ApiIcon,
  AccountTree as ConditionIcon,
  Loop as LoopIcon,
  Stop as EndIcon,
  CheckCircle as ValidateIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { NODE_ICONS, NodeType } from '../../../types/workflow.types';

interface WorkflowToolbarProps {
  readOnly?: boolean;
  onValidate?: () => void;
  onSave?: () => void;
}

const nodeDefinitions = [
  { type: 'start', label: 'Start', icon: <StartIcon />, color: '#4CAF50' },
  { type: 'teammate', label: 'AI Teammate', icon: <TeammateIcon />, color: '#2196F3' },
  { type: 'human', label: 'Human Task', icon: <HumanIcon />, color: '#FF9800' },
  { type: 'api', label: 'API Call', icon: <ApiIcon />, color: '#9C27B0' },
  { type: 'condition', label: 'Condition', icon: <ConditionIcon />, color: '#FFC107' },
  { type: 'loop', label: 'Loop', icon: <LoopIcon />, color: '#607D8B' },
  { type: 'end', label: 'End', icon: <EndIcon />, color: '#F44336' },
];

const WorkflowToolbar: React.FC<WorkflowToolbarProps> = ({
  readOnly = false,
  onValidate,
  onSave,
}) => {
  const onDragStart = (event: React.DragEvent, nodeType: string) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  if (readOnly) {
    return (
      <Paper
        elevation={2}
        style={{
          padding: 16,
          border: '1px solid rgba(0,0,0,0.12)',
        }}
      >
        <Typography variant="h6" gutterBottom>
          Workflow Viewer
        </Typography>
        <Typography variant="body2" style={{ color: 'rgba(0,0,0,0.6)' }}>
          Read-only mode - workflow cannot be edited
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={2}
      style={{
        padding: 16,
        border: '1px solid rgba(0,0,0,0.12)',
        maxWidth: 250,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <Typography variant="h6">
          Node Palette
        </Typography>
        <div style={{ display: 'flex', gap: 8 }}>
          {onSave && (
            <Tooltip title="Save Workflow">
              <IconButton size="small" onClick={onSave} color="primary">
                <SaveIcon />
              </IconButton>
            </Tooltip>
          )}
          {onValidate && (
            <Tooltip title="Validate Workflow">
              <IconButton size="small" onClick={onValidate} color="success">
                <ValidateIcon />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </div>

      <Typography variant="body2" style={{ color: 'rgba(0,0,0,0.6)', marginBottom: 16 }}>
        Drag nodes onto the canvas to build your workflow
      </Typography>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {nodeDefinitions.map((nodeDef) => (
          <Tooltip key={nodeDef.type} title={`Drag to add ${nodeDef.label}`} placement="right">
            <Paper
              style={{
                padding: 12,
                cursor: 'grab',
                border: '1px solid rgba(0,0,0,0.12)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
              draggable
              onDragStart={(event) => onDragStart(event, nodeDef.type)}
            >
              <div
                style={{
                  color: nodeDef.color,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {nodeDef.icon}
              </div>
              <Typography variant="body2" style={{ flex: 1 }}>
                {nodeDef.label}
              </Typography>
              <Chip
                label={NODE_ICONS[nodeDef.type as NodeType]}
                size="small"
                style={{
                  backgroundColor: nodeDef.color,
                  color: 'white',
                  fontSize: '12px',
                  height: 20,
                }}
              />
            </Paper>
          </Tooltip>
        ))}
      </div>

      <Divider style={{ marginTop: 16, marginBottom: 16 }} />

      <Typography variant="body2" style={{ color: 'rgba(0,0,0,0.6)', fontSize: '0.75rem' }}>
        <strong>Tips:</strong>
        <br />
        • Connect nodes by dragging from output to input handles
        <br />
        • Click nodes to configure properties
        <br />
        • Use the minimap to navigate large workflows
        <br />
        • Press space + drag to pan the canvas
      </Typography>
    </Paper>
  );
};

export default WorkflowToolbar;
